// Pure reducer for command palette keyboard state and section assembly.
//
// The reducer owns: query, scope, activeIndex, isOpen.
// Section assembly (which sections appear and in what order) is a pure
// useMemo derivation from the reducer state + drained data. The query fed
// into the sections memo is deferred via useDeferredValue — React can
// interrupt expensive filter passes on fast typing without input lag.
//
// Sections per scope/query:
//   empty query          → Recent, Navigation (all pre-gated nav items, unfiltered)
//   query + global scope → Contracts, Drafts, Records, Navigation (matched items only)
//   query + contract     → Tabs in this contract, Records in this contract,
//                          Contracts (when query ≥ 3 chars), Navigation
//
// Navigation is never capped — all pre-gated items are shown so a viewer's
// Settings link can never be pushed out by higher-scoring items.
//
// Arrow navigation wraps over a flat index spanning all visible candidates.
// Esc: clears query when non-empty; closes when already empty.
// Tab: toggles scope (contract ↔ global) when a contractId is provided.
//
// activeIndex pre-select: the reducer stores -1 after OPEN and 0 after
// SET_QUERY/ESC/TOGGLE_SCOPE. The hook clamps the index to
// [0, flatCandidates.length − 1] (or -1 when the list is empty) before
// exposing it, so the top result is always pre-selected once candidates
// arrive. This matches the authoritative prototype behaviour.
//
// Requests are deliberately NOT aborted on close — apiRequest does not
// consume React Query's AbortSignal; drains are idempotent GETs under stable
// keys; threading a signal through the api client is out of scope.

import { useReducer, useMemo, useDeferredValue } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { usePaletteSearchData } from "./usePaletteSearchData";
import { useNavRecents } from "../../hooks/useNavRecents";
import { buildNavigationGroups } from "../common/navigationRegistry";
import { CONTRACT_TABS } from "../manage/tabs/tabRegistry";
import {
  managedContractCandidates,
  draftContractCandidates,
  recordCandidates,
  invoiceCandidates,
  navCandidates,
  buildOverflowCandidate,
} from "./sources";
import { rankAndCap, matchText } from "./scoring";
import type {
  PaletteCandidate,
  ManagedContractCandidate,
  NavCandidate,
  TabCandidate,
  RecordCandidate,
} from "./paletteTypes";
import type { RecentEntry } from "../../hooks/useNavRecents";

// Re-export types the tests import from this module.
export type { ManagedContractCandidate, NavCandidate, TabCandidate };

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface PaletteState {
  query: string;
  scope: "global" | "contract";
  activeIndex: number;
  isOpen: boolean;
}

export type PaletteAction =
  | { type: "SET_QUERY"; query: string }
  | { type: "ESC" }
  /** scope defaults to 'global'; pass 'contract' when opening on a contract route. */
  | { type: "OPEN"; scope?: "global" | "contract" }
  | { type: "CLOSE" }
  | { type: "TOGGLE_SCOPE"; contractId: string | null }
  | { type: "MOVE_DOWN"; totalItems: number }
  | { type: "MOVE_UP"; totalItems: number }
  | { type: "SET_ACTIVE"; index: number };

/** A grouped section rendered in the palette listbox. */
export interface PaletteSection {
  /** Section identity key — used for aria-label and display heading. */
  kind: "recent" | "contracts" | "drafts" | "records" | "invoices" | "tabs" | "nav";
  /** Human-readable heading shown above the section. */
  label: string;
  /** Ordered candidates to render. May include an overflow sentinel. */
  candidates: PaletteCandidate[];
}

/** The entry shape for the recent section items (re-exported for tests). */
export type RecentSection = PaletteSection & { kind: "recent" };

// ─── Display caps ──────────────────────────────────────────────────────────────

const CAP_CONTRACTS = 5;
const CAP_DRAFTS = 5;
const CAP_RECORDS_PER_TYPE = 5;
const CAP_RECORDS_TOTAL = 20;
const CAP_INVOICES = 5;
// Navigation has no cap — all pre-gated items are shown (plan §Scoring).

// ─── Reducer ───────────────────────────────────────────────────────────────────

const INITIAL_STATE: PaletteState = {
  query: "",
  scope: "global",
  activeIndex: -1,
  isOpen: false,
};

export function paletteReducer(state: PaletteState, action: PaletteAction): PaletteState {
  switch (action.type) {
    case "OPEN":
      return { ...INITIAL_STATE, isOpen: true, scope: action.scope ?? "global" };

    case "CLOSE":
      return { ...state, isOpen: false };

    case "SET_QUERY":
      // Reset to 0 so the hook's clamp pre-selects the top result once
      // candidates arrive. The hook clamps to -1 when the list is empty.
      return { ...state, query: action.query, activeIndex: 0 };

    case "ESC":
      if (state.query !== "") {
        // Clear the query; reset to 0 so the top recent/nav item is
        // pre-selected in the empty-query state that follows.
        return { ...state, query: "", activeIndex: 0 };
      }
      return { ...state, isOpen: false };

    case "TOGGLE_SCOPE":
      if (!action.contractId) return state; // no contract context — noop
      return {
        ...state,
        scope: state.scope === "global" ? "contract" : "global",
        // Reset to 0 — candidate set changes on scope toggle; clamp to -1
        // if the new scope has no candidates.
        activeIndex: 0,
      };

    case "MOVE_DOWN": {
      if (action.totalItems === 0) return state;
      const next = state.activeIndex >= action.totalItems - 1 ? 0 : state.activeIndex + 1;
      return { ...state, activeIndex: next };
    }

    case "MOVE_UP": {
      if (action.totalItems === 0) return state;
      const next = state.activeIndex <= 0 ? action.totalItems - 1 : state.activeIndex - 1;
      return { ...state, activeIndex: next };
    }

    case "SET_ACTIVE":
      return { ...state, activeIndex: action.index };

    default:
      return state;
  }
}

// ─── Section assembly helpers ──────────────────────────────────────────────────

function recentSection(shown: RecentEntry[]): PaletteSection | null {
  if (shown.length === 0) return null;
  // Map RecentEntry to a minimal PaletteCandidate for display.
  const candidates: PaletteCandidate[] = shown.map((e) => {
    if (e.kind === "record") {
      return {
        kind: "record" as const,
        id: `recent:record:${e.id}`,
        title: e.title,
        ctx: e.ctx,
        url: e.url,
        score: 0,
        recordType: e.recordType,
        contractId: e.contractId,
      };
    }
    if (e.kind === "invoice") {
      return {
        kind: "invoice" as const,
        id: `recent:invoice:${e.id}`,
        title: e.title,
        ctx: e.ctx,
        url: e.url,
        score: 0,
        contractId: e.contractId,
        // Display-only on the candidate shape; the recent row renders title +
        // ctx + icon only, and no stored status exists to hydrate from.
        status: "",
      };
    }
    if (e.kind === "draft-contract") {
      return {
        kind: "draft-contract" as const,
        id: `recent:draft:${e.id}`,
        title: e.title,
        ctx: e.ctx,
        url: e.url,
        score: 0,
        status: "draft",
      };
    }
    // managed-contract (default)
    return {
      kind: "managed-contract" as const,
      id: `recent:mc:${e.id}`,
      title: e.title,
      ctx: e.ctx,
      url: e.url,
      score: 0,
      contractNumber: "",
      supplierName: "",
      status: "active",
    };
  });

  return { kind: "recent", label: "Recent", candidates };
}

/**
 * Build tab candidates for a given contract by matching the query against
 * each CONTRACT_TAB label via `matchText` — same tokenised AND-match as every
 * other candidate source, giving multi-token behaviour and titleRanges for
 * highlight rendering.
 */
function buildTabCandidates(contractId: string, query: string): TabCandidate[] {
  const results: TabCandidate[] = [];
  for (const tab of CONTRACT_TABS) {
    const params = new URLSearchParams({ tab: tab.slug });
    if (query) {
      const match = matchText(tab.label, query);
      if (!match) continue;
      results.push({
        kind: "tab",
        id: `tab:${contractId}:${tab.slug}`,
        title: tab.label,
        label: tab.label,
        url: `/manage/contracts/${encodeURIComponent(contractId)}?${params.toString()}`,
        score: match.strength,
        titleRanges: match.ranges,
      });
    } else {
      results.push({
        kind: "tab",
        id: `tab:${contractId}:${tab.slug}`,
        title: tab.label,
        label: tab.label,
        url: `/manage/contracts/${encodeURIComponent(contractId)}?${params.toString()}`,
        score: 0,
      });
    }
  }
  return results;
}

/** Group by recordType, cap each type, then cap the total — records must not
 *  let one noisy type (e.g. meetings) crowd out every other type. Shared by
 *  both the contract-scope and global-scope record sections. */
function capRecordCandidates(candidates: RecordCandidate[]): RecordCandidate[] {
  const byType = new Map<string, RecordCandidate[]>();
  for (const c of candidates) {
    const bucket = byType.get(c.recordType) ?? [];
    bucket.push(c);
    byType.set(c.recordType, bucket);
  }
  const perTypeCapped: RecordCandidate[] = [];
  for (const bucket of byType.values()) {
    for (const c of rankAndCap(bucket, CAP_RECORDS_PER_TYPE)) {
      perTypeCapped.push(c);
    }
  }
  return rankAndCap(perTypeCapped, CAP_RECORDS_TOTAL);
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export interface UsePaletteControllerParams {
  isOpen: boolean;
  contractId: string | null;
}

export interface UsePaletteControllerResult {
  state: PaletteState;
  dispatch: React.Dispatch<PaletteAction>;
  sections: PaletteSection[];
  /** Flat ordered list of all candidates across sections (for activeIndex tracking). */
  flatCandidates: PaletteCandidate[];
  /** URL of the currently active candidate, or null when activeIndex is -1. */
  activeCandidateUrl: string | null;
  isLoading: boolean;
  /** Global scope: some org record drains were truncated or failed. Only
   *  meaningful while the palette is open — CommandPalette returns null when
   *  closed, and the OPEN action resets state. */
  recordsIncomplete: boolean;
  /** record + clear from useNavRecents — exposed so PaletteProvider can drive them. */
  recordRecent: ReturnType<typeof useNavRecents>["record"];
  clearRecents: ReturnType<typeof useNavRecents>["clear"];
}

/**
 * Pure reducer and section assembly for the command palette.
 *
 * The reducer owns keyboard state; sections is a derived memo fed a deferred
 * query so React can interrupt expensive filter passes on fast typing.
 * Both are co-located here so CommandPalette.tsx stays a thin rendering shell.
 */
export function usePaletteController({
  isOpen,
  contractId,
}: UsePaletteControllerParams): UsePaletteControllerResult {
  const [state, dispatch] = useReducer(paletteReducer, INITIAL_STATE);

  const { user, hasArea } = useAuth();
  const { shown, record: recordRecent, clear: clearRecents } = useNavRecents();

  const { managedContracts, draftContracts, records, invoices, recordsIncomplete, isLoading } =
    usePaletteSearchData({
      open: isOpen,
      query: state.query,
      scope: state.scope,
      contractId,
    });

  const navGroups = buildNavigationGroups(user?.role, hasArea);

  // Build last-visited map for recency boost in managed-contract scoring.
  const lastVisitedMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of shown) {
      if (e.kind === "managed-contract") m.set(e.id, e.visitedAt);
    }
    return m;
  }, [shown]);

  // contractId → contract number for record-row attribution in global scope.
  const contractRefById = useMemo(() => {
    const m = new Map<string, string>();
    for (const mc of managedContracts) {
      m.set(mc.id, mc.contractNumber || mc.title);
    }
    return m;
  }, [managedContracts]);

  // Defer the query feeding section assembly — React can interrupt expensive
  // filter passes on fast typing without blocking the input.
  const deferredQuery = useDeferredValue(state.query);
  const trimmedQuery = deferredQuery.trim();

  // Section assembly — pure derivation over deferred query.
  const sections = useMemo((): PaletteSection[] => {
    if (!trimmedQuery) {
      // Empty query: show recents + all pre-gated nav items (no scoring filter).
      const result: PaletteSection[] = [];
      const recent = recentSection(shown);
      if (recent) result.push(recent);

      const allNavItems: NavCandidate[] = navGroups.flatMap((g) =>
        g.items.map((item) => ({
          kind: "nav" as const,
          id: `nav:${item.href}`,
          title: item.name,
          url: item.href,
          icon: item.icon,
          score: 0,
        })),
      );
      if (allNavItems.length > 0) {
        result.push({ kind: "nav", label: "Navigation", candidates: allNavItems });
      }
      return result;
    }

    // Non-empty query, contract scope
    if (state.scope === "contract" && contractId) {
      const result: PaletteSection[] = [];

      // 1. Tabs in this contract
      const tabItems = buildTabCandidates(contractId, trimmedQuery);
      if (tabItems.length > 0) {
        result.push({ kind: "tabs", label: "Tabs in this contract", candidates: tabItems });
      }

      // 2. Records in this contract — per-type cap of 5, then 20-total cap
      const cappedRecords = capRecordCandidates(recordCandidates(records, trimmedQuery));
      if (cappedRecords.length > 0 || isLoading) {
        result.push({ kind: "records", label: "Records in this contract", candidates: cappedRecords });
      }

      // 3. Contracts (only when query ≥ 3 chars)
      if (trimmedQuery.length >= 3) {
        const mcItems = managedContractCandidates(managedContracts, trimmedQuery, lastVisitedMap);
        const cappedMc = rankAndCap(mcItems, CAP_CONTRACTS);
        if (cappedMc.length > 0) {
          const candidates: PaletteCandidate[] = [...cappedMc];
          if (mcItems.length > CAP_CONTRACTS) {
            candidates.push(buildOverflowCandidate("managed-contract", trimmedQuery, mcItems.length));
          }
          result.push({ kind: "contracts", label: "Contracts", candidates });
        }
      }

      // 4. Navigation — all matching items, uncapped
      const navItems = navCandidates(navGroups, trimmedQuery);
      if (navItems.length > 0) {
        result.push({ kind: "nav", label: "Navigation", candidates: navItems });
      }

      return result;
    }

    // Global scope with query: Contracts, Drafts, Records, Navigation
    const result: PaletteSection[] = [];

    const mcItems = managedContractCandidates(managedContracts, trimmedQuery, lastVisitedMap);
    const cappedMc = rankAndCap(mcItems, CAP_CONTRACTS);
    if (cappedMc.length > 0) {
      const candidates: PaletteCandidate[] = [...cappedMc];
      if (mcItems.length > CAP_CONTRACTS) {
        candidates.push(buildOverflowCandidate("managed-contract", trimmedQuery, mcItems.length));
      }
      result.push({ kind: "contracts", label: "Contract Register", candidates });
    }

    const dcItems = draftContractCandidates(draftContracts, trimmedQuery);
    const cappedDc = rankAndCap(dcItems, CAP_DRAFTS);
    if (cappedDc.length > 0) {
      const candidates: PaletteCandidate[] = [...cappedDc];
      if (dcItems.length > CAP_DRAFTS) {
        candidates.push(buildOverflowCandidate("draft-contract", trimmedQuery, dcItems.length));
      }
      result.push({ kind: "drafts", label: "Document Drafts", candidates });
    }

    // Records across all contracts — same capping as contract scope; ctx
    // carries the owning contract's number so rows are attributable at a
    // glance.
    // nowMs omitted (undefined) so recordCandidates' own Date.now() default
    // applies — react-hooks/purity forbids calling impure functions directly
    // in the render body, so nowMs can't be computed here.
    const recordItems = recordCandidates(records, trimmedQuery, undefined, contractRefById);
    const cappedRecords = capRecordCandidates(recordItems);
    if (cappedRecords.length > 0 || isLoading) {
      result.push({ kind: "records", label: "Records", candidates: cappedRecords });
    }

    // Invoices across all contracts (global scope only — the finance tab's
    // own in-queue search covers contract scope). nowMs omitted for the same
    // react-hooks/purity reason as recordCandidates above.
    const invoiceItems = invoiceCandidates(invoices, trimmedQuery, contractRefById);
    const cappedInvoices = rankAndCap(invoiceItems, CAP_INVOICES);
    if (cappedInvoices.length > 0) {
      result.push({ kind: "invoices", label: "Invoices", candidates: cappedInvoices });
    }

    // Navigation — all matching items, uncapped
    const navItems = navCandidates(navGroups, trimmedQuery);
    if (navItems.length > 0) {
      result.push({ kind: "nav", label: "Navigation", candidates: navItems });
    }

    return result;
  }, [
    trimmedQuery,
    state.scope,
    contractId,
    managedContracts,
    draftContracts,
    records,
    invoices,
    navGroups,
    shown,
    lastVisitedMap,
    contractRefById,
    isLoading,
  ]);

  // Flat candidate list for activeIndex addressing
  const flatCandidates = useMemo(
    () => sections.flatMap((s) => s.candidates),
    [sections],
  );

  // Clamp the reducer's activeIndex to [0, flatCandidates.length − 1].
  // When the list is empty the effective index is -1 (no selection).
  // This implements prototype-conformant pre-select: the top result is always
  // highlighted as soon as candidates arrive without requiring ArrowDown.
  const effectiveActiveIndex = flatCandidates.length > 0
    ? Math.min(Math.max(state.activeIndex, 0), flatCandidates.length - 1)
    : -1;

  const activeCandidateUrl =
    effectiveActiveIndex >= 0
      ? flatCandidates[effectiveActiveIndex].url
      : null;

  // Surface the clamped index through state so CommandPalette's
  // aria-activedescendant and Enter handler always see the right value.
  const effectiveState: PaletteState = { ...state, activeIndex: effectiveActiveIndex };

  return {
    state: effectiveState,
    dispatch,
    sections,
    flatCandidates,
    activeCandidateUrl,
    isLoading,
    recordsIncomplete,
    recordRecent,
    clearRecents,
  };
}
